import React from 'react';
import { cn } from '../../lib/utils';

/**
 * ScoreBar - 10 vertical segments visualising a lead score.
 * Accepts either a 0-10 or 0-100 score (auto-detected via maxScore prop).
 * - Segments 1-3 fill in blue (low)
 * - Segments 4-6 fill in navy (medium)
 * - Segments 7-10 fill in emerald (hot)
 * Empty segments stay neutral slate.
 *
 * Props:
 * - score: number
 * - maxScore: number (default 100 — matches the existing compute_lead_score range)
 * - className: extra wrapper classes (set width/height)
 * - testId: data-testid passthrough
 */
const ScoreBar = ({ score = 0, maxScore = 100, className = '', testId }) => {
  const raw = Number(score) || 0;
  // Normalise into 0-10 segments (1 segment per 10% of max)
  const filledSegments = Math.max(0, Math.min(10, Math.round((raw / maxScore) * 10)));

  const colourFor = (idx) => {
    // idx is 1-based
    if (idx > filledSegments) return 'bg-slate-200 dark:bg-slate-700';
    if (idx <= 3) return 'bg-blue-500';
    if (idx <= 6) return 'bg-crm-navy';
    return 'bg-emerald-500';
  };

  return (
    <div
      className={cn('flex items-end gap-[2px] h-4 w-24', className)}
      data-testid={testId}
      aria-label={`Score ${raw} of ${maxScore}`}
    >
      {Array.from({ length: 10 }, (_, i) => (
        <span
          key={i}
          className={cn(
            'flex-1 h-full rounded-[1px] transition-colors',
            colourFor(i + 1),
          )}
        />
      ))}
    </div>
  );
};

export default ScoreBar;
